<template>
  <div
    class="accordion"
    :class="{
      'accordion--open': isOpen,
      'accordion--closed': !isOpen,
      'accordion--hide-arrow': foldOut,
    }"
    :id="anchor"
    ref="accordion"
    role="presentation"
    :aria-labelledby="`${uid}_head`"
  >
    <div
      class="accordion__headline"
      @click="toggleClass"
      @keydown.enter.space.prevent="toggleClass"
      role="button"
      :tabindex="foldOut === false ? 0 : null"
      :aria-expanded="isOpen"
      :id="`${uid}_head`"
      :aria-controls="`${uid}_section`"
    >
      <slot name="head" />
    </div>
    <div
      class="accordion__content"
      :inert="!isOpen"
      :id="`${uid}_section`"
      ref="accordionContent"
    >
      <slot name="content" />
    </div>
  </div>
</template>

<script lang="ts">
  interface AccordionProps {
    initialOpen?: boolean;
    foldOut?: boolean;
    useMaxHeight?: boolean;
    anchor?: string;
  }

  export {AccordionProps};
</script>

<script setup lang="ts">
  import {ref, watch, onMounted, useTemplateRef, useId} from 'vue';
  import * as scrollTo from 'scroll-to';

  const props = withDefaults(
    defineProps<AccordionProps>(),
    {
      initialOpen: false,
      foldOut: false,
      useMaxHeight: true,
      anchor: null
    }
  );

  const isOpen = ref(props.initialOpen || props.foldOut);
  const accordionEl = useTemplateRef('accordion');
  const accordionContentEl = useTemplateRef('accordionContent');
  const uid = useId();

  watch(() => props.initialOpen, (initialOpen) => {
    isOpen.value = initialOpen || props.foldOut;
    resizeContent();
  });

  onMounted(() => {
    const hash = document.location.hash;
    window.addEventListener('resize', () => {
      resizeContent();
    });
    if (props.anchor && hash.indexOf(props.anchor) >= 0) {
      isOpen.value = true;
      const accordion = accordionEl.value;
      const accordionRect = accordion.getBoundingClientRect();
      if (accordionRect && accordionRect.top) {
        const bodyRect = document.body.getBoundingClientRect();
        const offsetTop = accordionRect.top - bodyRect.top - 70;
        scrollTo(0, offsetTop, {duration: 500});
      }
    }
  });

  function resizeContent() {
    setMaxHeight();
  }

  function toggleClass() {
    if (props.foldOut) {
      return;
    }
    isOpen.value = !isOpen.value;
    setMaxHeight();
  }

  function setMaxHeight() {
    if (!props.useMaxHeight) return;

    const accordionContent = accordionContentEl.value;
    if (isOpen.value && accordionContent?.children) {
      const hasLazyContent = accordionContent.querySelector('.lazy-load, [loading="lazy"]') !== null;
      if (hasLazyContent) {
        accordionContent.style.maxHeight = '2000px';
      } else {
        accordionContent.style.maxHeight = (<HTMLElement>accordionContent.children[0]).offsetHeight + 'px';
      }
    }
    else {
      accordionContent.style.maxHeight = '0';
    }
  }
</script>
